import { useMemo } from "react";
import { Field, Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useDeleteOtaAnnotationMutation, useSaveOtaAnnotationMutation } from "@dashboard/rollouts/api";
import { TagManagementSection } from "@dashboard/shared/components/TagManagementSection";
import { DialogProps } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

type FormValues = {
    tags: API.Tag[];
};

type Props = Omit<DialogProps, "onClose" | "maxWidth" | "fullWidth"> & {
    codename: string;
    version: string;
    otaTags?: API.OtaAnnotation;
    onClose: () => void;
};

const metadataToTags = (metadata: Record<string, string>): API.Tag[] =>
    Object.entries(metadata).map(([key, value]): API.Tag => ({
        key,
        value,
        type: value ? "scoped" : "simple",
    }));

const tagsToMetadata = (tags: API.Tag[]): Record<string, string> =>
    Object.fromEntries(tags.map(({ key, value }) => [key, value ?? ""]));

export const OtaTagsDialog = ({ codename, version, otaTags, onClose, open, ...dialogProps }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const [saveOtaAnnotation] = useSaveOtaAnnotationMutation();
    const [deleteOtaAnnotation] = useDeleteOtaAnnotationMutation();
    const initialValues = useMemo(() => ({ tags: metadataToTags(otaTags?.metadata?.user ?? {}) }), [otaTags?.metadata?.user]);

    const onSubmit = async (values: FormValues) => {
        if (!initialValues.tags.length && !values.tags.length) {
            onClose();
            return;
        }
        if (workspaceId === null) {
            showErrorToast(t("common:toast.noWorkspaceIdFound"));
            return;
        }
        const metadata = tagsToMetadata(values.tags);
        let result;

        if (values.tags.length === 0) {
            result = await deleteOtaAnnotation({
                codename,
                version,
                workspaceId,
            });
        } else {
            result = await saveOtaAnnotation({
                codename,
                version,
                workspaceId,
                metadata,
            });
        }

        if ("error" in result) {
            showErrorToast(getErrorMessage(result.error));
            return;
        }
        showSuccessToast(t("modals:otaTags.toast.success"));
        onClose();
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit, form, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:otaTags.title")}
                        open={open}
                        {...dialogProps}
                        onClose={() => {
                            if (submitting) {
                                return;
                            }
                            onClose();
                        }}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={pristine || submitting}>
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <Field name="tags">
                            {({ input }) => <TagManagementSection tags={input.value ?? []} onChange={input.onChange} />}
                        </Field>
                    </Modal>
                </form>
            )}
        />
    );
};
